/**
 * The standing statement a fixture-backed surface carries, on the screen.
 *
 * ## Why this is shared rather than per chapter
 *
 * Six chapters had arrived at this independently and had arrived somewhere different, which
 * is the same situation `docs/adr/0015` found `AsyncRegion` and `ProblemNotice` in. Measured
 * on 2026-09-17, across 24 call sites:
 *
 * | chapter | what it rendered |
 * |---|---|
 * | `exit`, `preseason` | `StatusIndicator` warning + a standing label + the reason |
 * | `inseason` | the same shape, prop named `bodyKey` rather than `reasonKey` |
 * | `postseason` | a `StatusIndicator` whose label WAS the reason — no standing marker |
 * | `launch` | a bordered `<p role="note">` — no marker and no icon |
 * | `foundations` | a bare `<p>` of secondary text — no marker, no border, no role |
 *
 * So `LabsLtd/bata-ui#50` C1's premise — that fixture-backed screens do not say so — was
 * **false** when measured: every screen rendering fixture data said so. What was true, and
 * worse in one specific way, is that it said so in five presentations of three different
 * prominences. A reviewer sweeping six chapters in one sitting cannot learn a single visual
 * signal, and the three weakest read as an explainer rather than as a provenance warning —
 * which is exactly the reading that lets an illustrative number be cited.
 *
 * ## One line, not a panel — ADR 0083 as amended (audit v5b D-06)
 *
 * The statement was a filled box with an icon, a title and three to six lines of reason, once
 * per section; on `/inseason/open-to-buy` it outweighed the data it qualified. It is now a TAG
 * — the warning mark and the word "Illustrative" — in the section's header, beside the title,
 * always visible; and an amber rule down the inline start of the section's data. The reason is
 * one hover (`title`) or one press (a popover, keyboard-reachable) away. It is never removed:
 * a figure with no measurement behind it still cannot be read as a measurement, and the
 * reason is still in the chapter's words and still required.
 *
 * Inside a `Panel`, `FixtureProvenance` renders nothing where it is written: it REGISTERS its
 * reason with the panel, which draws the tag in its header and the rule on its body. That is
 * what keeps the 24 call sites and `census:fixtures` — which asserts a fixture-importing file
 * renders `<FixtureProvenance>` — exactly as they were. Outside a panel it draws the tag
 * itself, in place.
 *
 * ## The split between what is shared and what is not
 *
 * The **warning** is universal, so its label lives in `core` and is the same word on every
 * screen in the product. The **reason** is local and stays in the chapter's own namespace:
 * which panel has no measurement behind it, and which endpoint would close it, is a fact
 * about that chapter and is the useful half.
 *
 * `role="note"` on the tag's group, carried over from `launch`: nothing has failed. This is a
 * standing statement about the surface, and `role="alert"` would train a planner to dismiss
 * it. design/09 §12.6 — the icon and the word travel together, so the warning never rests on
 * the amber alone.
 */

import { createContext, useContext, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixPopover from '@radix-ui/react-popover';
import { TriangleAlert } from 'lucide-react';

import { FOCUS_RING } from '../primitives/focus';

/** A panel's registrar: adds a reason, returns the function that removes it again. */
type Register = (reasonKey: string) => () => void;

const IllustrativeRegistry = createContext<Register | null>(null);

/** Provided by `Panel`, so a `FixtureProvenance` inside it reports to the panel's header. */
export const IllustrativeProvider = IllustrativeRegistry.Provider;

export interface IllustrativeTagProps {
  /** One reason per fixture-backed part of the surface, each in its chapter's namespace. */
  readonly reasonKeys: readonly string[];
}

/**
 * "⚠ Illustrative" — the one-line provenance statement. A button, because it opens the
 * reason; `title` repeats the reason for the pointer, so hovering is enough to read it.
 */
export function IllustrativeTag({ reasonKeys }: IllustrativeTagProps): React.JSX.Element {
  const { t } = useTranslation();
  const reasons = reasonKeys.map((key) => t(key));
  return (
    <span role="note" className="inline-flex">
      <RadixPopover.Root>
        <RadixPopover.Trigger asChild>
          <button
            type="button"
            title={reasons.join('\n\n')}
            className={`border-status-warning bg-surface-sunken text-label text-ink-primary inline-flex items-center gap-1 rounded-chip border px-1 ${FOCUS_RING}`}
          >
            <span className="text-status-warning inline-flex">
              <TriangleAlert size={12} aria-hidden />
            </span>
            {t('provenance.tag')}
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            sideOffset={4}
            aria-label={t('provenance.tag')}
            className="bg-surface-raised border-popover-border shadow-popover z-popover flex max-h-[var(--radix-popover-content-available-height)] max-w-sm flex-col overflow-y-auto gap-2 rounded-control border p-3"
          >
            {/* The standing sentence — what "Illustrative" means, the same everywhere. */}
            <p className="text-label text-ink-primary">{t('provenance.illustrative')}</p>
            {reasons.map((reason) => (
              <p key={reason} className="text-caption text-ink-secondary">{reason}</p>
            ))}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    </span>
  );
}

export interface FixtureProvenanceProps {
  /**
   * ICU key in the CHAPTER's namespace, naming exactly which part of this surface has no
   * measurement behind it — and, where it is known, the endpoint that would close it.
   *
   * Required, and deliberately has no default. A generic "some of this is illustrative"
   * is the marker without the information: a planner cannot tell which column to distrust,
   * so they distrust the screen or, worse, none of it.
   */
  readonly reasonKey: string;
}

export function FixtureProvenance({ reasonKey }: FixtureProvenanceProps): React.JSX.Element | null {
  const register = useContext(IllustrativeRegistry);
  // Layout effect, so the panel's header carries the tag before the first paint rather than
  // one frame after it.
  useLayoutEffect(() => (register === null ? undefined : register(reasonKey)), [register, reasonKey]);
  if (register !== null) return null;
  return <IllustrativeTag reasonKeys={[reasonKey]} />;
}
